import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';

import { ActionKeys } from '../../../Common/Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { ConfigService } from '../../../Core/Config/services/config.service';

import { Mode, PasswordResetService } from './password-reset.service';
import { partnerResellerConfigs } from '../../config/resellers/reseller-config-model';
import { PartnerResellers } from '../../../Core/Config/enums/partner-resellers.enum';


export enum InputType {
    TEXT = 'text',
    PASSWORD = 'password'
}

export class Password {
    value: string;
    type: InputType = InputType.PASSWORD;

    toggleVisibility() {
        this.type = this.type === InputType.PASSWORD ? InputType.TEXT : InputType.PASSWORD;
    }

    hide() {
        this.type = InputType.PASSWORD;
    }

    reset() {
        this.value = '';
        this.hide();
    }

}

@Component({
    selector: 'agpr-password-reset',
    templateUrl: './password-reset.component.html',
    styleUrls: ['./password-reset.component.scss'],
    standalone: false
})
export class PasswordResetComponent implements OnDestroy {

    logoUrl: string;
    logoAltText: string;
    logoRouterLink = undefined;
    subscriptions: Subscription[] = [];

    username: string;
    firstName: string;
    passwords: Password[] = [];
    current = new Password();
    newPass = new Password();
    confirm = new Password();

    passwordValidators: ValidatorFn[] = [
        Validators.required,
        Validators.minLength(12),
        this.passwordResetService.passwordPatternValidator
    ];
    currentPasswordFormControl: FormControl<string> = new FormControl<string>(null, Validators.required);
    newPasswordFormControl: FormControl<string> = new FormControl<string>(null, this.passwordValidators);
    passwordResetForm: FormGroup = new FormGroup({
        username: new FormControl<string>(null),
        currentPassword: this.currentPasswordFormControl,
        newPassword: this.newPasswordFormControl,
        confirmPassword: new FormControl<string>(null, Validators.required),
    }, { validators: this.passwordResetService.passwordsMatchValidator });

    valid: boolean = false;
    disabledTooltip: string = 'PasswordReset.disabledTooltip.requirements';
    apiError: string;
    resetCompleted: boolean = false;

    loading: boolean;

    reseller: PartnerResellers;

    mode: Mode;
    readonly modes = Mode;
    readonly inputTypes = InputType;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public passwordResetService: PasswordResetService,
        public titleService: Title,
        public localizationService: LocalizationService,
        public router: Router,
        private configService: ConfigService
    ) {
        this.passwordResetService.setError$(null);
        this.logoUrl = appBaseHref + 'assets/images/agility-logo-horizontal.png';
        this.logoAltText = 'Core.login.agility';
        this.subscriptions.push(
            this.passwordResetService.getUsername$().subscribe(x => {
                this.username = x;
                this.passwordResetForm.get('username').setValue(x);
            }),
            this.passwordResetService.getFirstName$().subscribe(x => this.firstName = x),
            this.passwordResetService.getLoading$().subscribe(x => this.loading = x),
            this.passwordResetService.getMode$().subscribe(x => {
                this.mode = x;
                if (this.mode === Mode.RESET) {
                    this.currentPasswordFormControl.disable();
                }
                this.titleService.setTitle(
                    this.localizationService.transform(
                        this.mode === Mode.SET ? 'PasswordSet.browserTitle' :
                            this.mode === Mode.CHANGE ? 'PasswordChange.browserTitle' : 'PasswordReset.browserTitle'
                    )
                );
            }),
            this.passwordResetService.getError$().subscribe(error => {
                this.apiError = error;
            }),
            this.passwordResetForm.valueChanges.subscribe(change => {
                this.current.value = change.currentPassword;
                this.newPass.value = change.newPassword;
                this.confirm.value = change.confirmPassword;
                this.updateValidationTooltip();
            })
        );
        this.passwords.push(this.current, this.newPass, this.confirm);

        if (this.configService.hasBrandGUID()) {
            this.reseller = this.configService.getPartnerReseller();
            const resellerConfig = partnerResellerConfigs.get(this.reseller);
            if (resellerConfig) {
                this.logoUrl = resellerConfig.logoUrl;
                this.logoAltText = resellerConfig.logoAltText;
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    reset() {
        if (!this.passwordResetForm.valid) {
            return;
        }
        if (this.mode === Mode.RESET) {
            this.passwordResetService.setPassword(this.newPass.value);
            if (!this.apiError) {
                this.resetCompleted = true;
            }
        } else {
            this.passwordResetService.changePassword(this.current.value, this.newPass.value);
        }
        this.passwords.forEach(pass => pass.reset());
    }

    resetOnEnter(event: KeyboardEvent): void {
        if (event.key === ActionKeys.ENTER) {
            this.reset();
        }
    }

    updateValidationTooltip() {
        this.disabledTooltip = '';
        if (!this.passwordResetForm.valid) {
            if (this.passwordResetForm.errors) {
                this.disabledTooltip = 'PasswordReset.disabledTooltip.match';
            } else if (this.passwordResetForm.get('newPassword').valid) {
                this.disabledTooltip = 'PasswordReset.disabledTooltip.requirements';
            }
        }
        if ((this.mode === Mode.CHANGE || this.mode === Mode.SET) && this.current.value?.length === 0) {
            this.disabledTooltip = 'PasswordReset.disabledTooltip.current';
        }
    }

    hidePasswords(password: Password) {
        this.passwords.forEach(pass => {
            if (pass !== password) {
                pass.hide();
            }
        });
    }

    backToLogin() {
        this.router.navigate(['Login']);
    }

    skipForNow() {
        this.router.navigate(['Dashboard']);
    }

    get newPasswordErrors(): ValidationErrors {
        return this.newPasswordFormControl.errors;
    }
}
